import React, { ForwardedRef, ThHTMLAttributes } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/table/table.module.css';
import { ColStyleProps, ColumnAlignment } from '..';
import { getAlignmentStyles } from './utils';
import { DOMAttributes } from '@react-types/shared';
import { TooltipIfNeeded } from './TooltipIfNeeded';
import { mergeProps } from 'react-aria/mergeProps';
import { mergeRefs } from 'react-aria/mergeRefs';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export type TableCellProps = Omit<
  DOMAttributes<HTMLTableCellElement>,
  'className' | 'style' | 'dangerouslySetInnerHTML'
> &
  SapphireStyleProps & {
    /**
     * Note: Currently, we don't officially support non-table elements.
     *
     * @default 'td'
     */
    elementType?: 'td';

    children?: React.ReactNode;

    /**
     * A function that allows you to wrap the cell contents.
     *
     * This is useful for those cases where you need something in the cell that
     * is not affected by the cell's overflow behaviour.
     */
    wrapCellContent?: (cellContent: React.ReactNode) => React.ReactNode;

    /**
     * Tooltip text that's shown when overflow is set to "truncate" and the cell content doesn't fit.
     * Useful when the cell content is not plain text. If not provided, the text content of the cell
     * is used.
     * TODO(v6): rename to something like overflowTooltip that is more after what it does.
     */
    textValue?: React.ReactNode;

    // TODO(v6): remove null.
    width?: SapphireStyleProps['width'] | null;
    minWidth?: SapphireStyleProps['minWidth'] | null;
    maxWidth?: SapphireStyleProps['maxWidth'] | null;
  } & ColStyleProps &
  Pick<
    ThHTMLAttributes<HTMLTableCellElement>,
    'headers' | 'abbr' | 'scope' | 'colSpan' | 'rowSpan'
  >;

export function getCellClassNames({
  align,
  overflow,
}: {
  align: ColumnAlignment | undefined;
  overflow: 'wrap' | 'truncate' | undefined;
}): string {
  return clsx(styles['sapphire-table__cell'], getAlignmentStyles(align), {
    [styles['sapphire-table__cell--ellipsed']]: overflow === 'truncate',
  });
}

/**
 * Corresponding to `td` element in native html. Should be rendered inside
 * {@Link Table.Row}
 */

export const TableCell = React.forwardRef(function TableCell(
  {
    children,
    elementType,
    align,
    overflow = 'truncate',
    textValue,
    wrapCellContent,
    colSpan,
    rowSpan,
    ...props
  }: TableCellProps,
  forwardedRef: ForwardedRef<HTMLTableCellElement>
) {
  const ElementType = elementType || 'td';
  const role = props.role || ElementType === 'td' ? undefined : 'cell';

  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  const title =
    textValue ?? (typeof children === 'string' ? children : undefined);

  return (
    <ElementType
      ref={forwardedRef}
      {...mergeProps(filteredProps, { role, colSpan, rowSpan })}
      className={clsx(
        getCellClassNames({ align: align, overflow: overflow }),
        styleProps.className
      )}
      style={styleProps.style}
    >
      <Wrapped wrap={wrapCellContent}>
        <TooltipIfNeeded<HTMLTableCellElement> title={title}>
          {(ref, tooltipTriggerProps) => {
            const { ref: positionRef, ...restTooltipProps } =
              tooltipTriggerProps || {};
            return (
              <span
                ref={mergeRefs(ref, positionRef)}
                {...restTooltipProps}
                className={clsx(styles['sapphire-table__cell-contents'])}
              >
                {children}
              </span>
            );
          }}
        </TooltipIfNeeded>
      </Wrapped>
    </ElementType>
  );
});

const Wrapped = ({
  children,
  wrap,
}: {
  children: React.ReactNode;
  wrap?: (content: React.ReactNode) => React.ReactNode;
}) => {
  if (wrap) {
    return <>{wrap(children)}</>;
  }
  return <>{children}</>;
};
